import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merge conditional class names, letting later Tailwind utilities win over earlier ones. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Shorten a ULID-suffixed identifier for table cells: `txn_01JZ…8QK4`. */
export function shortId(id: string): string {
  const underscore = id.indexOf('_');
  if (underscore === -1 || id.length <= underscore + 13) return id;
  const prefix = id.slice(0, underscore + 1);
  const body = id.slice(underscore + 1);
  return `${prefix}${body.slice(0, 4)}…${body.slice(-4)}`;
}

/** Title-case a `SCREAMING_SNAKE_CASE` enum member: `UNDER_REVIEW` → `Under review`. */
export function humanizeEnum(value: string): string {
  const lower = value.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Percentage of `value` within `total`, clamped to `[0, 100]`, as a rounded integer. */
export function percentOf(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((value / total) * 100)));
}

/** Clamp `value` into `[min, max]`. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Stable, dependency-free array grouping. */
export function groupBy<T, K extends string>(
  items: readonly T[],
  key: (item: T) => K,
): Record<K, T[]> {
  const out = {} as Record<K, T[]>;
  for (const item of items) {
    const k = key(item);
    (out[k] ??= []).push(item);
  }
  return out;
}

/** Initials for an avatar, working for both Latin and Thai display names. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}
